import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useReducedMotion } from 'framer-motion'
import {
  CalendarDays,
  Clock,
  Users,
  User,
  Phone,
  Mail,
  MessageSquare,
  Loader2,
} from 'lucide-react'
import { PageMeta } from '@/components/ui/PageMeta'
import { cn } from '@/lib/utils'
import {
  getAvailableSlots,
  isDateClosed,
  parseDateInput,
} from '@/lib/booking'
import {
  clearLastBookingId,
  getBooking,
  getLastBookingId,
  saveBooking,
  type Booking,
} from '@/lib/mockBookings'

export function Book() {
  const { t, i18n } = useTranslation('book')
  const shouldReduceMotion = useReducedMotion()
  const lang = i18n.language as 'it' | 'en'

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const canonicalPath = `/${lang}/${lang === 'it' ? 'prenota' : 'book'}`

  const today = useMemo(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  }, [])

  const maxDate = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 6)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])

  const [currentBookingId, setCurrentBookingId] = useState<string | null>(
    getLastBookingId()
  )
  const [currentBooking, setCurrentBooking] = useState<Booking | null>(
    currentBookingId ? getBooking(currentBookingId) ?? null : null
  )
  const [status, setStatus] = useState<'idle' | 'submitting'>('idle')
  const [formError, setFormError] = useState(false)
  const [dateError, setDateError] = useState(false)

  // Form fields
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [guests, setGuests] = useState('2')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')

  const selectedDate = date ? parseDateInput(date) : null
  const slots = useMemo(
    () => (selectedDate ? getAvailableSlots(selectedDate) : []),
    [selectedDate]
  )
  const closed = selectedDate ? isDateClosed(selectedDate) : false

  const lunchSlots = slots.filter((slot) => slot.period === 'lunch')
  const dinnerSlots = slots.filter((slot) => slot.period === 'dinner')

  // Poll localStorage so if the owner confirms via the admin dashboard
  // in the same browser, the customer view updates automatically.
  useEffect(() => {
    if (!currentBookingId) return
    const update = () => {
      setCurrentBooking(getBooking(currentBookingId) ?? null)
    }
    update()
    const interval = setInterval(update, 2000)
    return () => clearInterval(interval)
  }, [currentBookingId])

  const handleDateChange = (value: string) => {
    if (!value) {
      setDate('')
      setTime('')
      setDateError(false)
      setFormError(false)
      return
    }
    const picked = parseDateInput(value)
    const min = parseDateInput(today)
    const max = parseDateInput(maxDate)
    if (picked < min || picked > max) {
      setDateError(true)
      setDate('')
      setTime('')
      return
    }
    setDate(value)
    setTime('')
    setDateError(false)
    setFormError(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!date || !time || !name.trim() || !phone.trim() || !email.trim() || !guests) {
      setFormError(true)
      return
    }
    const picked = parseDateInput(date)
    const min = parseDateInput(today)
    const max = parseDateInput(maxDate)
    if (picked < min || picked > max) {
      setDateError(true)
      return
    }
    setFormError(false)
    setDateError(false)
    setStatus('submitting')

    const booking = saveBooking({
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
      guests: Number(guests),
      date,
      time,
      notes: notes.trim(),
      locale: lang,
    })

    setCurrentBookingId(booking.id)
    setCurrentBooking(booking)
    setStatus('idle')

    // Reset form for a future new request
    setDate('')
    setTime('')
    setGuests('2')
    setName('')
    setPhone('')
    setEmail('')
    setNotes('')
  }

  const handleNewBooking = () => {
    clearLastBookingId()
    setCurrentBookingId(null)
    setCurrentBooking(null)
  }

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        canonicalPath={canonicalPath}
      />

      <main id="main-content">
        <section className="relative flex min-h-screen items-center bg-maia px-4 py-16 md:py-24 lg:px-8">
          <div className="mx-auto grid min-w-0 w-full max-w-7xl items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="text-center text-white lg:text-left">
              <CalendarDays
                className="mx-auto mb-6 h-16 w-16 opacity-90 md:h-20 md:w-20 lg:mx-0"
                strokeWidth={1}
                aria-hidden="true"
              />
              <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] font-extrabold leading-[1.1] tracking-tight">
                {t('hero.title')}
              </h1>
              <p className="mx-auto mt-5 max-w-xl text-lg font-medium leading-relaxed opacity-95 md:text-xl lg:mx-0">
                {t('hero.subtitle')}
              </p>
            </div>

            <div className="mx-auto w-full min-w-0 max-w-xl">
              {currentBooking ? (
                <BookingStatusCard
                  booking={currentBooking}
                  onNewBooking={handleNewBooking}
                  t={t}
                  fadeUp={fadeUp}
                />
              ) : (
                <motion.form
                  {...fadeUp}
                  onSubmit={handleSubmit}
                  className="min-w-0 space-y-8 rounded-3xl bg-white p-6 shadow-sm sm:p-10"
                >
                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6 sm:grid-cols-2">
                    {/* Date */}
                    <label className="block min-w-0">
                      <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                        <CalendarDays
                          className="h-4 w-4 text-maia"
                          aria-hidden="true"
                        />
                        {t('form.dateLabel')}
                      </span>
                      <div className="relative flex min-w-0">
                        <input
                          type="date"
                          min={today}
                          max={maxDate}
                          value={date}
                          onChange={(e) => handleDateChange(e.target.value)}
                          required
                          className="w-full min-w-0 max-w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
                        />
                      </div>
                      {dateError && (
                        <p className="mt-2 text-sm font-medium text-pizza">
                          {t('form.dateRange')}
                        </p>
                      )}
                    </label>

                    {/* Guests */}
                    <label className="block min-w-0">
                      <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                        <Users className="h-4 w-4 text-maia" aria-hidden="true" />
                        {t('form.guestsLabel')}
                      </span>
                      <select
                        value={guests}
                        onChange={(e) => {
                          setGuests(e.target.value)
                          setFormError(false)
                        }}
                        required
                        className="block w-full min-w-0 rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
                      >
                        {Array.from({ length: 20 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {/* Time slots */}
                  <div role="group" aria-labelledby="time-legend" className="min-w-0">
                    <span
                      id="time-legend"
                      className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink"
                    >
                      <Clock className="h-4 w-4 text-maia" aria-hidden="true" />
                      {t('form.timeLabel')}
                    </span>
                    {!date ? (
                      <p className="text-sm text-muted">{t('form.noTime')}</p>
                    ) : closed ? (
                      <p className="text-sm font-medium text-pizza">
                        {t('form.closed')}
                      </p>
                    ) : (
                      <div className="min-w-0 space-y-5">
                        {lunchSlots.length > 0 && (
                          <div className="min-w-0">
                            <span className="text-xs font-bold uppercase tracking-wider text-maia-dark">
                              {t('form.lunch')}
                            </span>
                            <div className="mt-2 flex w-full min-w-0 max-w-full gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                              {lunchSlots.map((slot) => (
                                <button
                                  key={slot.value}
                                  type="button"
                                  onClick={() => {
                                    setTime(slot.value)
                                    setFormError(false)
                                  }}
                                  className={cn(
                                    'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors',
                                    time === slot.value
                                      ? 'bg-maia text-white'
                                      : 'border border-cord bg-cream text-ink hover:border-maia hover:text-maia'
                                  )}
                                >
                                  {slot.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {dinnerSlots.length > 0 && (
                          <div className="min-w-0">
                            <span className="text-xs font-bold uppercase tracking-wider text-maia-dark">
                              {t('form.dinner')}
                            </span>
                            <div className="mt-2 flex w-full min-w-0 max-w-full gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                              {dinnerSlots.map((slot) => (
                                <button
                                  key={slot.value}
                                  type="button"
                                  onClick={() => {
                                    setTime(slot.value)
                                    setFormError(false)
                                  }}
                                  className={cn(
                                    'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors',
                                    time === slot.value
                                      ? 'bg-maia text-white'
                                      : 'border border-cord bg-cream text-ink hover:border-maia hover:text-maia'
                                  )}
                                >
                                  {slot.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-6 sm:grid-cols-2">
                    {/* Name */}
                    <label className="block min-w-0">
                      <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                        <User className="h-4 w-4 text-maia" aria-hidden="true" />
                        {t('form.nameLabel')}
                      </span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value)
                          setFormError(false)
                        }}
                        required
                        className="block w-full min-w-0 rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
                      />
                    </label>

                    {/* Phone */}
                    <label className="block min-w-0">
                      <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                        <Phone
                          className="h-4 w-4 text-maia"
                          aria-hidden="true"
                        />
                        {t('form.phoneLabel')}
                      </span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value)
                          setFormError(false)
                        }}
                        required
                        className="block w-full min-w-0 rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
                      />
                    </label>

                    {/* Email */}
                    <label className="block sm:col-span-2">
                      <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                        <Mail className="h-4 w-4 text-maia" aria-hidden="true" />
                        {t('form.emailLabel')}
                        <span className="text-xs font-normal text-muted">
                          {t('form.emailRequiredHint')}
                        </span>
                      </span>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value)
                          setFormError(false)
                        }}
                        required
                        className="block w-full min-w-0 rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
                      />
                    </label>
                  </div>

                  {/* Notes */}
                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                      <MessageSquare
                        className="h-4 w-4 text-maia"
                        aria-hidden="true"
                      />
                      {t('form.notesLabel')}
                    </span>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={4}
                      className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
                    />
                  </label>

                  {formError && (
                    <p className="text-sm font-medium text-pizza">
                      {t('form.errorFill')}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full rounded-full bg-maia px-8 py-4 text-sm font-bold text-white shadow-lg transition-colors hover:bg-maia-dark disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === 'submitting' ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('form.submitting')}
                      </span>
                    ) : (
                      t('form.submit')
                    )}
                  </button>
                </motion.form>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

function BookingStatusCard({
  booking,
  onNewBooking,
  t,
  fadeUp,
}: {
  booking: Booking
  onNewBooking: () => void
  t: (key: string) => string
  fadeUp: Record<string, unknown>
}) {
  const isConfirmed = booking.status === 'confirmed'
  const dateTime = useMemo(() => {
    const date = new Date(`${booking.date}T${booking.time}`)
    return new Intl.DateTimeFormat(booking.locale === 'it' ? 'it-IT' : 'en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }, [booking])

  return (
    <motion.div
      {...fadeUp}
      className="rounded-3xl bg-white p-8 text-center shadow-sm sm:p-12"
    >
      <div
        className={cn(
          'mx-auto flex h-16 w-16 items-center justify-center rounded-full',
          isConfirmed ? 'bg-maia/10 text-maia' : 'bg-pizza/10 text-pizza'
        )}
      >
        <CalendarDays className="h-8 w-8" aria-hidden="true" />
      </div>

      <h3
        className={cn(
          'mt-6 font-display text-2xl font-bold',
          isConfirmed ? 'text-maia-dark' : 'text-ink'
        )}
      >
        {isConfirmed ? t('status.confirmedTitle') : t('status.pendingTitle')}
      </h3>

      <p className="mx-auto mt-3 max-w-md text-lg text-muted">
        {isConfirmed
          ? t('status.confirmedMessage')
          : t('status.pendingMessage')}
      </p>

      <div className="mx-auto mt-8 max-w-sm space-y-3 rounded-2xl bg-cream p-6 text-left">
        <div className="flex justify-between">
          <span className="text-muted">{t('status.code')}</span>
          <span className="font-mono font-semibold text-ink">
            #{booking.id.slice(0, 8)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted">{t('status.when')}</span>
          <span className="text-right font-semibold text-ink">{dateTime}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted">{t('status.guests')}</span>
          <span className="font-semibold text-ink">{booking.guests}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted">{t('status.email')}</span>
          <span className="font-semibold text-ink">{booking.email}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onNewBooking}
        className="mt-8 rounded-full border-2 border-cord bg-white px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-maia hover:text-maia"
      >
        {t('status.newBooking')}
      </button>
    </motion.div>
  )
}
